import { DiffsHighlighter, getSharedHighlighter, SupportedLanguages } from "@pierre/diffs";
import React, {
  Children,
  Suspense,
  isValidElement,
  use,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { openInPreferredEditor } from "../editorPreferences";
import { resolveDiffThemeName, type DiffThemeName } from "../lib/diffRendering";
import { fnv1a32 } from "../lib/diffRendering";
import { LRUCache } from "../lib/lruCache";
import { useTheme } from "../hooks/useTheme";
import { resolveMarkdownFileLinkTarget } from "../markdown-links";
import { readNativeApi } from "../nativeApi";
import {
  ChatDashboardLink,
  parseArtifactDashboardHref,
} from "../tools/dashboards/artifacts/ChatDashboardCard";
import { TextOutput } from "./TextOutput";

class CodeHighlightErrorBoundary extends React.Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { fallback: ReactNode; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

interface ChatMarkdownProps {
  text: string;
  cwd: string | undefined;
  isStreaming?: boolean;
  onWorkspaceFileLinkClick?: ((workspacePath: string) => void) | undefined;
}

const CODE_FENCE_LANGUAGE_REGEX = /(?:^|\s)language-([^\s]+)/;
const MAX_HIGHLIGHT_CACHE_ENTRIES = 500;
const MAX_HIGHLIGHT_CACHE_MEMORY_BYTES = 50 * 1024 * 1024;
const STREAMING_MARKDOWN_RENDER_INTERVAL_MS = 75;
const highlightedCodeCache = new LRUCache<string>(
  MAX_HIGHLIGHT_CACHE_ENTRIES,
  MAX_HIGHLIGHT_CACHE_MEMORY_BYTES,
);
const highlighterPromiseCache = new Map<string, Promise<DiffsHighlighter>>();
const WORKSPACE_FILE_LINK_PREFIX = "/workspace";
const POSITION_SUFFIX_PATTERN = /:\d+(?::\d+)?$/;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function stripSearchAndHash(value: string): string {
  const hashIndex = value.indexOf("#");
  const pathWithSearch = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const queryIndex = pathWithSearch.indexOf("?");
  return queryIndex >= 0 ? pathWithSearch.slice(0, queryIndex) : pathWithSearch;
}

function resolveWorkspaceFileLinkTarget(href: string | undefined): string | null {
  if (!href) {
    return null;
  }

  const decodedPath = safeDecode(stripSearchAndHash(href.trim()));
  if (!decodedPath.startsWith(WORKSPACE_FILE_LINK_PREFIX)) {
    return null;
  }

  return decodedPath.replace(POSITION_SUFFIX_PATTERN, "");
}

function extractFenceLanguage(className: string | undefined): string {
  const match = className?.match(CODE_FENCE_LANGUAGE_REGEX);
  return match?.[1] ?? "text";
}

function nodeToPlainText(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map((child) => nodeToPlainText(child)).join("");
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return nodeToPlainText(node.props.children);
  }
  return "";
}

function extractCodeBlock(
  children: ReactNode,
): { className: string | undefined; code: string } | null {
  const childNodes = Children.toArray(children);
  if (childNodes.length !== 1) {
    return null;
  }

  const onlyChild = childNodes[0];
  if (
    !isValidElement<{ className?: string; children?: ReactNode }>(onlyChild) ||
    onlyChild.type !== "code"
  ) {
    return null;
  }

  return {
    className: onlyChild.props.className,
    code: nodeToPlainText(onlyChild.props.children),
  };
}

function createHighlightCacheKey(code: string, language: string, themeName: DiffThemeName): string {
  return `${fnv1a32(code).toString(36)}:${code.length}:${language}:${themeName}`;
}

function estimateHighlightedSize(html: string, code: string): number {
  return Math.max(html.length * 2, code.length * 3);
}

function getHighlighterPromise(language: string): Promise<DiffsHighlighter> {
  const cached = highlighterPromiseCache.get(language);
  if (cached) return cached;

  const promise = getSharedHighlighter({
    themes: [resolveDiffThemeName("dark"), resolveDiffThemeName("light")],
    langs: [language as SupportedLanguages],
    preferredHighlighter: "shiki-js",
  }).catch((err) => {
    highlighterPromiseCache.delete(language);
    if (language === "text") {
      // "text" itself failed — Shiki cannot initialize at all, surface the error
      throw err;
    }
    // Language not supported by Shiki — fall back to "text"
    return getHighlighterPromise("text");
  });
  highlighterPromiseCache.set(language, promise);
  return promise;
}

function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function useStreamingMarkdownText(text: string, isStreaming: boolean): string {
  const [renderedText, setRenderedText] = useState(text);
  const pendingTextRef = useRef(text);
  const lastFlushAtRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    pendingTextRef.current = text;

    const flush = () => {
      if (flushTimerRef.current != null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      lastFlushAtRef.current = nowMs();
      setRenderedText((current) =>
        current === pendingTextRef.current ? current : pendingTextRef.current,
      );
    };

    const resetRequired = text.length < renderedText.length || !text.startsWith(renderedText);
    if (!isStreaming || resetRequired) {
      flush();
      return;
    }
    if (renderedText === text) {
      return;
    }
    const elapsedMs = nowMs() - lastFlushAtRef.current;
    if (lastFlushAtRef.current === 0 || elapsedMs >= STREAMING_MARKDOWN_RENDER_INTERVAL_MS) {
      flush();
      return;
    }
    if (flushTimerRef.current === null) {
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        lastFlushAtRef.current = nowMs();
        setRenderedText((current) =>
          current === pendingTextRef.current ? current : pendingTextRef.current,
        );
      }, STREAMING_MARKDOWN_RENDER_INTERVAL_MS - elapsedMs);
    }
  }, [isStreaming, renderedText, text]);

  useEffect(
    () => () => {
      if (flushTimerRef.current != null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    },
    [],
  );

  return renderedText;
}

interface SuspenseShikiCodeBlockProps {
  className: string | undefined;
  code: string;
  themeName: DiffThemeName;
  isStreaming: boolean;
}

function SuspenseShikiCodeBlock({
  className,
  code,
  themeName,
  isStreaming,
}: SuspenseShikiCodeBlockProps) {
  const fenceLanguage = extractFenceLanguage(className);
  // Shiki doesn't bundle a gitignore grammar; ini is a close match (#685).
  const language = fenceLanguage === "gitignore" ? "ini" : fenceLanguage;
  const cacheKey = createHighlightCacheKey(code, language, themeName);
  const cachedHighlightedHtml = !isStreaming ? highlightedCodeCache.get(cacheKey) : null;

  if (cachedHighlightedHtml != null) {
    return (
      <div
        className="chat-markdown-shiki"
        dangerouslySetInnerHTML={{ __html: cachedHighlightedHtml }}
      />
    );
  }

  const highlighter = use(getHighlighterPromise(language));
  const highlightedHtml = useMemo(() => {
    try {
      return highlighter.codeToHtml(code, { lang: language, theme: themeName });
    } catch (error) {
      // Log highlighting failures for debugging while falling back to plain text
      console.warn(
        `Code highlighting failed for language "${language}", falling back to plain text.`,
        error instanceof Error ? error.message : error,
      );
      // If highlighting fails for this language, render as plain text
      return highlighter.codeToHtml(code, { lang: "text", theme: themeName });
    }
  }, [code, highlighter, language, themeName]);

  useEffect(() => {
    if (!isStreaming) {
      highlightedCodeCache.set(
        cacheKey,
        highlightedHtml,
        estimateHighlightedSize(highlightedHtml, code),
      );
    }
  }, [cacheKey, code, highlightedHtml, isStreaming]);

  return (
    <div className="chat-markdown-shiki" dangerouslySetInnerHTML={{ __html: highlightedHtml }} />
  );
}

function ChatMarkdown({
  text,
  cwd,
  isStreaming = false,
  onWorkspaceFileLinkClick,
}: ChatMarkdownProps) {
  const { resolvedTheme } = useTheme();
  const diffThemeName = resolveDiffThemeName(resolvedTheme);
  const renderedText = useStreamingMarkdownText(text, isStreaming);
  const markdownComponents = useMemo<Components>(
    () => ({
      p({ node, ...props }) {
        const hasDashboardCard = node?.children.some(
          (child) =>
            child.type === "element" &&
            child.tagName === "a" &&
            parseArtifactDashboardHref(String(child.properties.href ?? "")) !== null,
        );
        return hasDashboardCard ? <div {...props} className="my-2.5" /> : <p {...props} />;
      },
      a({ node: _node, href, ...props }) {
        const dashboard = parseArtifactDashboardHref(href);
        if (dashboard) return <ChatDashboardLink {...props} href={href} dashboard={dashboard} />;

        const workspaceFilePath = resolveWorkspaceFileLinkTarget(href);
        if (workspaceFilePath && onWorkspaceFileLinkClick) {
          return (
            <a
              {...props}
              href={href}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onWorkspaceFileLinkClick(workspaceFilePath);
              }}
            />
          );
        }

        const targetPath = resolveMarkdownFileLinkTarget(href, cwd);
        if (!targetPath) {
          return <a {...props} href={href} target="_blank" rel="noreferrer" />;
        }

        return (
          <a
            {...props}
            href={href}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              const api = readNativeApi();
              if (api) {
                void openInPreferredEditor(api, targetPath);
              } else {
                console.warn("Native API not found. Unable to open file in editor.");
              }
            }}
          />
        );
      },
      pre({ node: _node, children, ...props }) {
        const codeBlock = extractCodeBlock(children);
        if (!codeBlock) {
          return (
            <TextOutput
              title="Text"
              copyText={nodeToPlainText(children)}
              className="chat-markdown-codeblock"
            >
              <pre {...props}>{children}</pre>
            </TextOutput>
          );
        }

        return (
          <TextOutput
            title={extractFenceLanguage(codeBlock.className)}
            copyText={codeBlock.code}
            copyLabel="Copy code"
            className="chat-markdown-codeblock"
          >
            <CodeHighlightErrorBoundary fallback={<pre {...props}>{children}</pre>}>
              <Suspense fallback={<pre {...props}>{children}</pre>}>
                <SuspenseShikiCodeBlock
                  className={codeBlock.className}
                  code={codeBlock.code}
                  themeName={diffThemeName}
                  isStreaming={isStreaming}
                />
              </Suspense>
            </CodeHighlightErrorBoundary>
          </TextOutput>
        );
      },
    }),
    [cwd, diffThemeName, isStreaming, onWorkspaceFileLinkClick],
  );

  return (
    <div className="chat-markdown w-full min-w-0 text-sm leading-relaxed text-foreground/80">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {renderedText}
      </ReactMarkdown>
    </div>
  );
}

export default memo(ChatMarkdown);
